import React, { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import DurationInputWithUnit from "../../../ui/DurationInput";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import {
  fetchGeneralSettings,
  updateGeneralSettingsThunk,
} from "../../../../redux/features/settingsSlice";
import {
  GeneralSettings,
  GeneralSettingsStatus,
  PAYMENT_PROVIDER_OPTIONS,
  SETTINGS_STATUS_OPTIONS,
  UpdateGeneralSettingsPayload,
  VERIFY_PROVIDER_OPTIONS,
} from "../../../../redux/features/types/settingsTypes";
import {
  convertFromSeconds,
  convertToSeconds,
  convertToUnit,
} from "../../../../helpers";

interface EditGeneralSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type DurationField = {
  key: keyof Pick<
    GeneralSettings,
    | "paymentDuration"
    | "disputeAppealDuration"
    | "acceptInitialOrderDuration"
    | "acceptOrderDuration"
    | "requestReturnDuration"
    | "dispatchOrderWaitingDuration"
    | "buyerRequestBatchTimeout"
    | "buyerRequestExpiryTimeout"
  >;
  label: string;
};

const DURATION_FIELDS: readonly DurationField[] = [
  { key: "paymentDuration", label: "Payment duration" },
  { key: "disputeAppealDuration", label: "Dispute appeal duration" },
  { key: "acceptInitialOrderDuration", label: "Accept initial order duration" },
  { key: "acceptOrderDuration", label: "Accept order duration" },
  { key: "requestReturnDuration", label: "Request return duration" },
  { key: "dispatchOrderWaitingDuration", label: "Dispatch order waiting duration" },
  { key: "buyerRequestBatchTimeout", label: "Buyer request batch timeout" },
  { key: "buyerRequestExpiryTimeout", label: "Buyer request expiry timeout" },
] as const;

type DurationState = { value: number; unit: string };
type DurationsState = Record<DurationField["key"], DurationState>;

const DEFAULT_DURATION: DurationState = { value: 1, unit: "days" };

const buildInitialDurations = (
  general: GeneralSettings | null
): DurationsState => {
  const out = {} as DurationsState;
  for (const field of DURATION_FIELDS) {
    const seconds = general?.[field.key];
    out[field.key] =
      typeof seconds === "number" ? convertToUnit(seconds) : { ...DEFAULT_DURATION };
  }
  return out;
};

const SectionHeading: FC<{ title: string }> = ({ title }) => (
  <h3 className="text-sm font-semibold text-gray-900 mb-3 mt-5 first:mt-0">
    {title}
  </h3>
);

/**
 * Small inline toggle. Mirrors the pattern used in PaymentMethodsTable —
 * promote to /pages/ui when a 3rd consumer shows up.
 */
const ToggleRow: FC<{
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <p className="text-sm font-medium text-gray-800">{label}</p>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
        checked ? "bg-primary" : "bg-gray-300"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

const EditGeneralSettingsModal: React.FC<EditGeneralSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { loading, general } = useAppSelector(
    (state: RootState) => state.settings
  );
  const settings = general as GeneralSettings | null;

  // ---- Form state -------------------------------------------------------
  const [durations, setDurations] = useState<DurationsState>(() =>
    buildInitialDurations(settings)
  );

  const [status, setStatus] = useState<GeneralSettingsStatus>(
    settings?.status ?? "ACTIVE"
  );

  // Providers
  const [defaultPaymentProvider, setDefaultPaymentProvider] = useState<string>(
    settings?.defaultPaymentProvider ?? "safe_haven"
  );
  const [defaultBVNVerifyProvider, setDefaultBVNVerifyProvider] = useState<string>(
    settings?.defaultBVNVerifyProvider ?? "safe_haven"
  );
  const [defaultNINVerifyProvider, setDefaultNINVerifyProvider] = useState<string>(
    settings?.defaultNINVerifyProvider ?? "safe_haven"
  );
  const [defaultCACVerifyProvider, setDefaultCACVerifyProvider] = useState<string>(
    settings?.defaultCACVerifyProvider ?? "safe_haven"
  );

  // Marketing program
  const [referralBonusAmount, setReferralBonusAmount] = useState<string>(
    String(settings?.referralBonusAmount ?? "")
  );
  const [marketerMinTransactionAmount, setMarketerMinTransactionAmount] =
    useState<string>(String(settings?.marketerMinTransactionAmount ?? ""));
  const [marketerPurchasesNeeded, setMarketerPurchasesNeeded] = useState<string>(
    String(settings?.marketerPurchasesNeeded ?? "")
  );
  const [marketerTotalRewardAmount, setMarketerTotalRewardAmount] =
    useState<string>(String(settings?.marketerTotalRewardAmount ?? ""));
  const [isMarketerRegistrationOpen, setIsMarketerRegistrationOpen] =
    useState<boolean>(settings?.isMarketerRegistrationOpen ?? false);

  // Coupons & rewards (toggles only — signupBonusAmount is server-managed)
  const [enableSignupBonusCoupon, setEnableSignupBonusCoupon] = useState<boolean>(
    settings?.enableSignupBonusCoupon ?? false
  );
  const [enableFirstOrderDiscountCoupon, setEnableFirstOrderDiscountCoupon] =
    useState<boolean>(settings?.enableFirstOrderDiscountCoupon ?? false);

  // Platform
  const [supportPhoneNumber, setSupportPhoneNumber] = useState<string>(
    settings?.supportPhoneNumber ?? ""
  );
  const [vendorIncentivePercentage, setVendorIncentivePercentage] =
    useState<string>(String(settings?.vendorIncentivePercentage ?? ""));
  const [buyerDiscountPercentage, setBuyerDiscountPercentage] = useState<string>(
    String(settings?.buyerDiscountPercentage ?? "")
  );
  const [numberOfVideoUploadsAllowed, setNumberOfVideoUploadsAllowed] =
    useState<string>(String(settings?.numberOfVideoUploadsAllowed ?? ""));

  // Hydrate from latest server snapshot whenever the modal opens or the
  // underlying settings change. Avoids stale form state across opens.
  useEffect(() => {
    if (!open) return;
    if (!settings) return;
    setDurations(buildInitialDurations(settings));
    setStatus(settings.status ?? "ACTIVE");
    setDefaultPaymentProvider(settings.defaultPaymentProvider ?? "safe_haven");
    setDefaultBVNVerifyProvider(settings.defaultBVNVerifyProvider ?? "safe_haven");
    setDefaultNINVerifyProvider(settings.defaultNINVerifyProvider ?? "safe_haven");
    setDefaultCACVerifyProvider(settings.defaultCACVerifyProvider ?? "safe_haven");
    setReferralBonusAmount(String(settings.referralBonusAmount ?? ""));
    setMarketerMinTransactionAmount(
      String(settings.marketerMinTransactionAmount ?? "")
    );
    setMarketerPurchasesNeeded(String(settings.marketerPurchasesNeeded ?? ""));
    setMarketerTotalRewardAmount(
      String(settings.marketerTotalRewardAmount ?? "")
    );
    setIsMarketerRegistrationOpen(!!settings.isMarketerRegistrationOpen);
    setEnableSignupBonusCoupon(!!settings.enableSignupBonusCoupon);
    setEnableFirstOrderDiscountCoupon(!!settings.enableFirstOrderDiscountCoupon);
    setSupportPhoneNumber(settings.supportPhoneNumber ?? "");
    setVendorIncentivePercentage(String(settings.vendorIncentivePercentage ?? ""));
    setBuyerDiscountPercentage(String(settings.buyerDiscountPercentage ?? ""));
    setNumberOfVideoUploadsAllowed(
      String(settings.numberOfVideoUploadsAllowed ?? "")
    );
  }, [open, settings]);

  // ---- Duration helpers -------------------------------------------------
  const handleDurationChange = (
    field: DurationField["key"],
    newValue: number,
    newUnit: string
  ) => {
    setDurations((prev) => {
      const current = prev[field];
      let valueToUse = newValue;
      if (newUnit !== current.unit) {
        const valueInSeconds = convertToSeconds(current.value, current.unit);
        valueToUse = convertFromSeconds(valueInSeconds, newUnit);
      }
      return {
        ...prev,
        [field]: {
          value: valueToUse < 0 ? 0 : valueToUse,
          unit: newUnit,
        },
      };
    });
  };

  // ---- Validation --------------------------------------------------------
  const parseNonNegativeNumber = (raw: string): number => {
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const validationError = useMemo<string | null>(() => {
    if (!settings) return "Settings have not loaded yet.";
    const phone = supportPhoneNumber.trim();
    if (!phone) return "Support phone number is required.";

    const numericFields: { label: string; raw: string }[] = [
      { label: "Referral bonus amount", raw: referralBonusAmount },
      {
        label: "Marketer min. transaction amount",
        raw: marketerMinTransactionAmount,
      },
      { label: "Marketer purchases needed", raw: marketerPurchasesNeeded },
      {
        label: "Marketer total reward amount",
        raw: marketerTotalRewardAmount,
      },
      {
        label: "Vendor incentive percentage",
        raw: vendorIncentivePercentage,
      },
      { label: "Buyer discount percentage", raw: buyerDiscountPercentage },
      {
        label: "Number of video uploads allowed",
        raw: numberOfVideoUploadsAllowed,
      },
    ];
    for (const { label, raw } of numericFields) {
      if (raw.trim() === "") return `${label} is required.`;
      const n = Number(raw);
      if (Number.isNaN(n)) return `${label} must be a valid number.`;
      if (n < 0) return `${label} cannot be negative.`;
    }

    const vp = Number(vendorIncentivePercentage);
    const bp = Number(buyerDiscountPercentage);
    if (vp > 100) return "Vendor incentive percentage cannot exceed 100%.";
    if (bp > 100) return "Buyer discount percentage cannot exceed 100%.";

    return null;
  }, [
    settings,
    supportPhoneNumber,
    referralBonusAmount,
    marketerMinTransactionAmount,
    marketerPurchasesNeeded,
    marketerTotalRewardAmount,
    vendorIncentivePercentage,
    buyerDiscountPercentage,
    numberOfVideoUploadsAllowed,
  ]);

  // ---- Submit ------------------------------------------------------------
  const handleSubmit = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (!settings) return;

    const payload: UpdateGeneralSettingsPayload = {
      id: settings.id,
      status,
      // Durations — convert form unit back to seconds for the wire.
      paymentDuration: convertToSeconds(
        durations.paymentDuration.value,
        durations.paymentDuration.unit
      ),
      disputeAppealDuration: convertToSeconds(
        durations.disputeAppealDuration.value,
        durations.disputeAppealDuration.unit
      ),
      acceptInitialOrderDuration: convertToSeconds(
        durations.acceptInitialOrderDuration.value,
        durations.acceptInitialOrderDuration.unit
      ),
      acceptOrderDuration: convertToSeconds(
        durations.acceptOrderDuration.value,
        durations.acceptOrderDuration.unit
      ),
      requestReturnDuration: convertToSeconds(
        durations.requestReturnDuration.value,
        durations.requestReturnDuration.unit
      ),
      dispatchOrderWaitingDuration: convertToSeconds(
        durations.dispatchOrderWaitingDuration.value,
        durations.dispatchOrderWaitingDuration.unit
      ),
      buyerRequestBatchTimeout: convertToSeconds(
        durations.buyerRequestBatchTimeout.value,
        durations.buyerRequestBatchTimeout.unit
      ),
      buyerRequestExpiryTimeout: convertToSeconds(
        durations.buyerRequestExpiryTimeout.value,
        durations.buyerRequestExpiryTimeout.unit
      ),
      // Providers
      defaultPaymentProvider,
      defaultBVNVerifyProvider,
      defaultNINVerifyProvider,
      defaultCACVerifyProvider,
      // Marketing
      referralBonusAmount: parseNonNegativeNumber(referralBonusAmount),
      marketerMinTransactionAmount: parseNonNegativeNumber(
        marketerMinTransactionAmount
      ),
      marketerPurchasesNeeded: parseNonNegativeNumber(marketerPurchasesNeeded),
      marketerTotalRewardAmount: parseNonNegativeNumber(
        marketerTotalRewardAmount
      ),
      isMarketerRegistrationOpen,
      // Coupons & rewards (signupBonusAmount is server-managed; omitted)
      enableSignupBonusCoupon,
      enableFirstOrderDiscountCoupon,
      // Platform
      supportPhoneNumber: supportPhoneNumber.trim(),
      vendorIncentivePercentage: parseNonNegativeNumber(
        vendorIncentivePercentage
      ),
      buyerDiscountPercentage: parseNonNegativeNumber(buyerDiscountPercentage),
      numberOfVideoUploadsAllowed: parseNonNegativeNumber(
        numberOfVideoUploadsAllowed
      ),
    };

    try {
      await dispatch(updateGeneralSettingsThunk(payload)).unwrap();
      toast.success("Settings updated successfully");
      // The slice's update handler has a known bug where its post-update
      // payload is undefined for this endpoint shape — re-fetching is the
      // safest way to refresh state.settings.general for the View modal.
      dispatch(fetchGeneralSettings());
      onClose();
    } catch (err) {
      // Thunk's rejected case already surfaces a toast.
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit General Settings"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Discard"
            onClick={onClose}
            layout="inline" variant="ghost"
          />
          <Button
            label="Save"
            onClick={handleSubmit}
            isLoading={loading}
            layout="inline"
          />
        </div>
      }
    >
      <div className="p-6 w-full">
        {!settings && (
          <p className="text-sm text-gray-500">
            Loading settings… If this persists, close and try again.
          </p>
        )}

        {settings && (
          <>
            {/* Lifecycle ----------------------------------------------- */}
            <SectionHeading title="Lifecycle" />
            <Dropdown
              label="Status"
              name="status"
              options={SETTINGS_STATUS_OPTIONS}
              placeholder="Select status"
              value={status}
              onSelect={(v) => setStatus(v as GeneralSettingsStatus)}
              required
            />

            {/* Durations ---------------------------------------------- */}
            <SectionHeading title="Durations" />
            {DURATION_FIELDS.map(({ key, label }) => (
              <DurationInputWithUnit
                key={key}
                label={label}
                value={durations[key].value}
                unit={durations[key].unit}
                onChange={(val, unit) => handleDurationChange(key, val, unit)}
              />
            ))}

            {/* Providers ---------------------------------------------- */}
            <SectionHeading title="Default providers" />
            <Dropdown
              label="Default payment provider"
              name="defaultPaymentProvider"
              options={PAYMENT_PROVIDER_OPTIONS}
              placeholder="Select provider"
              value={defaultPaymentProvider}
              onSelect={(v) => setDefaultPaymentProvider(v)}
              required
            />
            <Dropdown
              label="Default BVN verify provider"
              name="defaultBVNVerifyProvider"
              options={VERIFY_PROVIDER_OPTIONS}
              placeholder="Select provider"
              value={defaultBVNVerifyProvider}
              onSelect={(v) => setDefaultBVNVerifyProvider(v)}
              required
            />
            <Dropdown
              label="Default NIN verify provider"
              name="defaultNINVerifyProvider"
              options={VERIFY_PROVIDER_OPTIONS}
              placeholder="Select provider"
              value={defaultNINVerifyProvider}
              onSelect={(v) => setDefaultNINVerifyProvider(v)}
              required
            />
            <Dropdown
              label="Default CAC verify provider"
              name="defaultCACVerifyProvider"
              options={VERIFY_PROVIDER_OPTIONS}
              placeholder="Select provider"
              value={defaultCACVerifyProvider}
              onSelect={(v) => setDefaultCACVerifyProvider(v)}
              required
            />

            {/* Marketing program -------------------------------------- */}
            <SectionHeading title="Marketing program" />
            <ToggleRow
              label="Marketer registration open"
              checked={isMarketerRegistrationOpen}
              onChange={setIsMarketerRegistrationOpen}
            />
            <ModalInput
              label="Referral bonus (₦)"
              name="referralBonusAmount"
              type="number"
              value={referralBonusAmount}
              onChange={(e) => setReferralBonusAmount(e.target.value)}
              placeholder="0"
              min={0}
            />
            <ModalInput
              label="Marketer min. transaction amount (₦)"
              name="marketerMinTransactionAmount"
              type="number"
              value={marketerMinTransactionAmount}
              onChange={(e) =>
                setMarketerMinTransactionAmount(e.target.value)
              }
              placeholder="0"
              min={0}
            />
            <ModalInput
              label="Marketer purchases needed"
              name="marketerPurchasesNeeded"
              type="number"
              value={marketerPurchasesNeeded}
              onChange={(e) => setMarketerPurchasesNeeded(e.target.value)}
              placeholder="0"
              min={0}
            />
            <ModalInput
              label="Marketer total reward amount (₦)"
              name="marketerTotalRewardAmount"
              type="number"
              value={marketerTotalRewardAmount}
              onChange={(e) => setMarketerTotalRewardAmount(e.target.value)}
              placeholder="0"
              min={0}
            />

            {/* Coupons & rewards -------------------------------------- */}
            <SectionHeading title="Coupons & rewards" />
            <ToggleRow
              label="Auto-issue signup bonus coupon"
              checked={enableSignupBonusCoupon}
              onChange={setEnableSignupBonusCoupon}
            />
            <ToggleRow
              label="Auto-issue first order discount coupon"
              checked={enableFirstOrderDiscountCoupon}
              onChange={setEnableFirstOrderDiscountCoupon}
            />
            <div className="flex justify-between items-baseline text-xs text-gray-500 mt-2 py-1.5">
              <span>Signup bonus amount</span>
              <span className="text-gray-700 font-medium">
                ₦
                {Number(settings.signupBonusAmount ?? 0).toLocaleString(
                  "en-NG",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}
              </span>
            </div>

            {/* Platform ----------------------------------------------- */}
            <SectionHeading title="Platform" />
            <ModalInput
              label="Support phone number"
              name="supportPhoneNumber"
              type="tel"
              value={supportPhoneNumber}
              onChange={(e) => setSupportPhoneNumber(e.target.value)}
              placeholder="+234 …"
              required
            />
            <ModalInput
              label="Vendor incentive percentage (%)"
              name="vendorIncentivePercentage"
              type="number"
              value={vendorIncentivePercentage}
              onChange={(e) => setVendorIncentivePercentage(e.target.value)}
              placeholder="0"
              min={0}
              max={100}
            />
            <ModalInput
              label="Buyer discount percentage (%)"
              name="buyerDiscountPercentage"
              type="number"
              value={buyerDiscountPercentage}
              onChange={(e) => setBuyerDiscountPercentage(e.target.value)}
              placeholder="0"
              min={0}
              max={100}
            />
            <ModalInput
              label="Number of video uploads allowed"
              name="numberOfVideoUploadsAllowed"
              type="number"
              value={numberOfVideoUploadsAllowed}
              onChange={(e) =>
                setNumberOfVideoUploadsAllowed(e.target.value)
              }
              placeholder="0"
              min={0}
            />

            {validationError && (
              <p className="text-red-500 text-sm mt-3">{validationError}</p>
            )}
          </>
        )}
      </div>
    </CustomModalContainer>
  );
};

export default EditGeneralSettingsModal;
